/**
 * Passpoint — Hospitality > Passpoint (Hotspot 2.0 / 802.11u).
 *
 * Per-SSID ANQP/venue config so a provisioned device auto-connects with no captive portal.
 * Reuses the Meridian list+dialog pattern (GuestPortal). This configures discoverability
 * (venue, domain, roaming consortium, NAI realm); real Passpoint AUTH needs the SSID to be
 * enterprise (802.1X) pointed at a RADIUS server — a hint is shown when it isn't.
 *
 * Per-guest PROVISIONING already ships — it lives on Network → RADIUS Realms (Radius.tsx's
 * ProvisionDialog, POST /radius/realms/{id}/provision, which returns an installable .mobileconfig
 * or PPS-MO). It is not repeated here; instead this page POINTS at it, because a hospitality
 * operator lives on this screen and had no way to discover it (2026-09-20). A scan-to-install QR
 * is still genuinely missing: it needs a one-time tokenised download URL (the profile itself is
 * far too large to encode), which is a security design, not a widget.
 *
 * OP#1776 (2026-10-12): the list now shows PER-AP state -- "Partial" when only some APs carry
 * the block (it used to read the first AP only, so two dark APs showed "off") -- and the dialog
 * refuses to enable Passpoint on a non-802.1X SSID (the backend answers 400 for the same).
 */
import { useState } from "react";
import { ApiError } from "@/lib/api/client";
import { passpointPill } from "@/lib/ssidSafety";
import { Link } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import { useSession } from "@/stores/session";
import { usePasspoint, useSetPasspoint } from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { RoleGate } from "@/components/auth/RoleGate";
import type { Passpoint } from "@/types/domain";

// Common 802.11u venue group/type pairs (a hotel operator rarely needs the full table).
const VENUE_PRESETS: { label: string; group: number; type: number }[] = [
  { label: "Hotel / Motel", group: 7, type: 2 },
  { label: "Business", group: 2, type: 0 },
  { label: "Assembly", group: 1, type: 0 },
  { label: "Educational", group: 3, type: 0 },
];

export default function Passpoint() {
  const siteId = useSession((s) => s.currentSiteId);
  const siteName = useSession((s) => s.currentSite()?.name);
  const { data, isLoading, error } = usePasspoint(siteId);
  const [editing, setEditing] = useState<Passpoint | null>(null);

  if (!siteId) {
    return (
      <div>
        <PageHeader title="Passpoint" subtitle="Hotspot 2.0 / seamless secure onboarding" />
        <EmptyState title="Select a site first" hint="Passpoint applies to a site's Wi-Fi. Pick a site from the scope bar above." />
      </div>
    );
  }

  const rows = data?.passpoint ?? [];

  return (
    <div>
      <PageHeader title="Passpoint" subtitle={siteName ? `Hotspot 2.0 in ${siteName}` : undefined} />
      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Loading…</div>}

      {/* Discoverability, not duplication: the enrolment flow is one screen away and an
          operator on the Hospitality page had no signpost to it. */}
      <p className="mb-3 text-sm text-ink-muted">
        Passpoint here controls how the network <em>advertises</em> itself. To hand a guest a
        profile that auto-joins it, issue a credential under{" "}
        <Link to="../radius-realms" className="text-brand underline underline-offset-2">
          Network → RADIUS Realms
        </Link>
        .
      </p>

      {data && data.owgw_available === false && (
        <EmptyState title="No manageable APs" hint="No AP in this site has a live configuration." />
      )}
      {data && data.owgw_available && rows.length === 0 && (
        <EmptyState title="No SSIDs yet" hint="Create a Wi-Fi network first, then enable Passpoint on it here." />
      )}

      {rows.length > 0 && (
        <ul className="flex flex-col gap-2">
          {rows.map((p) => (
            <li key={p.ssid} className="md-card flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium text-ink">{p.ssid}</span>
                  {passpointPill(p).tone !== "off" ? (
                    <StatusPill status={passpointPill(p).tone === "warn" ? "warn" : "healthy"}>
                      {passpointPill(p).text}
                    </StatusPill>
                  ) : (
                    <span className="rounded border border-line px-1.5 py-0.5 text-[0.625rem] uppercase tracking-wide text-ink-subtle">
                      off
                    </span>
                  )}
                  {!p.enterprise && (
                    <span className="rounded border border-critical/40 px-1.5 py-0.5 text-[0.625rem] uppercase tracking-wide text-critical" title="Passpoint auth needs an enterprise (802.1X) SSID with a RADIUS server.">
                      not enterprise
                    </span>
                  )}
                </div>
                {(p.invalid_devices?.length ?? 0) > 0 && (
                  <p className="mt-0.5 text-xs text-critical">
                    Passpoint is on a non-enterprise SSID on {p.invalid_devices!.length} AP
                    {p.invalid_devices!.length === 1 ? "" : "s"} ({p.invalid_devices!.join(", ")}): the AP rejects
                    that radio and every SSID on it is off the air. Turn Passpoint off here to clear it.
                  </p>
                )}
                {p.state === "partial" && p.devices && (
                  <details className="mt-0.5 text-xs text-ink-muted">
                    <summary className="cursor-pointer">Per-AP state</summary>
                    <ul className="mt-1 flex flex-col gap-0.5">
                      {p.devices.map((d) => (
                        <li key={d.serial} className="font-mono">
                          {d.name}: {d.enabled ? "Passpoint on" : "off"}
                          {d.enabled && !d.enterprise ? " (not enterprise: radio rejected)" : ""}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                {p.enabled && (
                  <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-ink-muted">
                    {p.venue_name && <span>{p.venue_name}</span>}
                    {p.domain_name.length > 0 && <span className="font-mono">{p.domain_name.join(", ")}</span>}
                    {p.roaming_consortium.length > 0 && <span>{p.roaming_consortium.length} RC-OI</span>}
                  </div>
                )}
              </div>
              <RoleGate min="site_operator" mode="hide">
                <Button variant="secondary" onClick={() => setEditing(p)}>
                  {p.enabled ? "Edit" : "Set up"}
                </Button>
              </RoleGate>
            </li>
          ))}
        </ul>
      )}

      {editing && <PasspointDialog siteId={siteId} row={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function PasspointDialog({ siteId, row, onClose }: { siteId: string; row: Passpoint; onClose: () => void }) {
  const set = useSetPasspoint(siteId);
  const [enabled, setEnabled] = useState(row.enabled);
  const [venue, setVenue] = useState(row.venue_name ?? "");
  const [friendly, setFriendly] = useState(row.friendly_name ?? "");
  // hostapd needs "<lang>:<name>"; the backend adds the prefix (default eng). OP#1776.
  const [lang, setLang] = useState(row.venue_language ?? "eng");
  const [venueUrl, setVenueUrl] = useState(row.venue_url ?? "");
  const presetIdx = VENUE_PRESETS.findIndex((v) => v.group === row.venue_group && v.type === row.venue_type);
  const [preset, setPreset] = useState(presetIdx >= 0 ? String(presetIdx) : "0");
  const [domains, setDomains] = useState(row.domain_name.join("\n"));
  const [rc, setRc] = useState(row.roaming_consortium.join("\n"));
  const [realms, setRealms] = useState(row.nai_realm.map((r) => r.realm).join("\n"));
  // One EAP method applies to all entered realms (the common single-realm hotel case).
  // Seed from the first stored realm's method; fall back to the username/password default.
  const [naiMethod, setNaiMethod] = useState(row.nai_realm[0]?.method || "eap-ttls-pap");
  const [internet, setInternet] = useState(row.internet);

  const lines = (s: string) => s.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = VENUE_PRESETS[Number(preset)] ?? VENUE_PRESETS[0]!;
    set.mutate(
      {
        ssid: row.ssid,
        enabled,
        ...(enabled
          ? {
              venue_name: venue.trim() || undefined,
              friendly_name: friendly.trim() || undefined,
              venue_language: lang.trim().toLowerCase() || "eng",
              venue_url: venueUrl.trim() || undefined,
              venue_group: v.group,
              venue_type: v.type,
              domain_name: lines(domains),
              roaming_consortium: lines(rc),
              // Structured {realm, method} so hostapd advertises a matchable EAP method.
              nai_realm: lines(realms).map((realm) => ({ realm, method: naiMethod })),
              internet,
            }
          : {}),
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">Passpoint — {row.ssid}</Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            802.11u discoverability so provisioned devices auto-connect. Auth still needs an
            enterprise SSID + RADIUS server.
          </Dialog.Description>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              <span>Enable Passpoint (Hotspot 2.0)</span>
            </label>

            {enabled && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="text-ink-muted">Venue name</span>
                    <input value={venue} onChange={(e) => setVenue(e.target.value)} placeholder="Hotel Downtown"
                      className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand" />
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="text-ink-muted">Venue type</span>
                    <select value={preset} onChange={(e) => setPreset(e.target.value)}
                      className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand">
                      {VENUE_PRESETS.map((v, i) => (
                        <option key={v.label} value={i}>{v.label}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="grid grid-cols-[6rem,1fr] gap-3">
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="text-ink-muted">Language</span>
                    <input value={lang} onChange={(e) => setLang(e.target.value)} maxLength={3} placeholder="eng"
                      title="ISO-639-2 code (3 letters) for the venue and operator names"
                      className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand" />
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="text-ink-muted">Venue URL (optional)</span>
                    <input value={venueUrl} onChange={(e) => setVenueUrl(e.target.value)} placeholder="https://hotel.example/info"
                      className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand" />
                  </label>
                </div>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-ink-muted">Operator name (shown on the device)</span>
                  <input value={friendly} onChange={(e) => setFriendly(e.target.value)} placeholder="Downtown Hotel"
                    className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand" />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-ink-muted">Domain name(s)</span>
                  <textarea value={domains} onChange={(e) => setDomains(e.target.value)} rows={2}
                    placeholder="hotel.example.com"
                    className="rounded border border-line bg-surface px-3 py-2 font-mono text-sm text-ink outline-none focus:border-brand" />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-ink-muted">Roaming consortium OIs (hex)</span>
                  <textarea value={rc} onChange={(e) => setRc(e.target.value)} rows={2}
                    placeholder="004096 / 001bc50460"
                    className="rounded border border-line bg-surface px-3 py-2 font-mono text-sm text-ink outline-none focus:border-brand" />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-ink-muted">NAI realm(s)</span>
                  <textarea value={realms} onChange={(e) => setRealms(e.target.value)} rows={2}
                    placeholder="hotel.example.com"
                    className="rounded border border-line bg-surface px-3 py-2 font-mono text-sm text-ink outline-none focus:border-brand" />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-ink-muted">EAP method (how devices authenticate to these realms)</span>
                  <select value={naiMethod} onChange={(e) => setNaiMethod(e.target.value)}
                    className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand">
                    <option value="eap-ttls-pap">EAP-TTLS / PAP (username &amp; password)</option>
                    <option value="eap-peap-mschapv2">EAP-PEAP / MSCHAPv2 (username &amp; password)</option>
                    <option value="eap-tls">EAP-TLS (client certificate)</option>
                  </select>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={internet} onChange={(e) => setInternet(e.target.checked)} />
                  <span>Advertise internet access</span>
                </label>
                {!row.enterprise && (
                  <p role="alert" className="rounded border border-critical/40 bg-surface-2 px-3 py-2 text-xs text-critical">
                    Passpoint can&rsquo;t be enabled on this SSID: it isn&rsquo;t WPA2/WPA3-Enterprise (802.1X) on every
                    AP. On an Open or passphrase SSID the AP rejects the whole radio, which takes every SSID on it off
                    the air. Change the SSID to Enterprise with a RADIUS server under Wireless → SSIDs first.
                  </p>
                )}
              </>
            )}

            {set.isError && (
              <span className="text-xs text-critical">
                Could not save Passpoint
                {set.error instanceof ApiError && (set.error.body as { error?: string } | null)?.error
                  ? `: ${(set.error.body as { error: string }).error}`
                  : "."}
              </span>
            )}
            {set.data && (
              <span className="text-xs text-healthy">Pushed to {set.data.pushed} AP{set.data.pushed === 1 ? "" : "s"}.</span>
            )}
            <div className="md-dialog-footer flex items-center justify-end gap-2">
              <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
              {/* Enabling on a non-802.1X SSID is refused (the backend 400s too); turning it
                  OFF is always allowed -- that is how a leftover block is cleared. */}
              <Button variant="primary" type="submit" disabled={set.isPending || (enabled && !row.enterprise)}>
                {set.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
